import React from 'react';
import { Building2, ShieldOff, LifeBuoy } from 'lucide-react';

/**
 * Écran affiché lorsqu'une adresse ne correspond à aucun établissement, ou à
 * un établissement suspendu.
 *
 * Pourquoi une page plutôt qu'une adresse morte : nginx sert un joker
 * `*.klasys.com` et ne connaît pas la liste des écoles. Lui apprendre à
 * refuser les sous-domaines inconnus supposerait un fichier régénéré à chaque
 * création et suppression — un point de synchronisation de plus entre le CIK
 * et KLASYS, donc une dérive de plus.
 *
 * Et surtout, une page blanche ou une connexion refusée ne se distingue pas
 * d'une panne : celui qui tape l'adresse conclut que KLASYS est cassé. Un
 * message nommé lui dit ce qui se passe et coupe court à l'appel au support.
 *
 * L'accès réseau d'un établissement, lui, se restreint au niveau réseau —
 * Tailscale ou réseau local — et non en rendant les messages d'erreur flous.
 */

const CAS = {
  TENANT_NOT_FOUND: {
    icone: Building2,
    titre: 'Aucun établissement à cette adresse',
    corps: (sousDomaine) => (
      <>
        L'adresse <strong>{sousDomaine}</strong> ne correspond à aucun établissement.
        Vérifie l'orthographe du sous-domaine, ou rapproche-toi de la personne qui
        t'a communiqué le lien.
      </>
    )
  },
  TENANT_SUSPENDED: {
    icone: ShieldOff,
    titre: 'Accès suspendu',
    corps: (sousDomaine) => (
      <>
        L'accès à <strong>{sousDomaine}</strong> est momentanément suspendu.
        Les données de l'établissement sont intactes&nbsp;: seul l'accès est fermé.
        Contacte l'administrateur de ton établissement pour en connaître la raison.
      </>
    )
  }
};

const EtablissementIndisponible = ({ code, sousDomaine }) => {
  const cas = CAS[code] || CAS.TENANT_NOT_FOUND;
  const Icone = cas.icone;

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center">
        <div className="mx-auto w-16 h-16 rounded-full bg-[#246990]/10 flex items-center justify-center mb-6">
          <Icone size={30} className="text-[#246990]" />
        </div>

        <h1 className="text-2xl font-semibold text-gray-900 mb-3">
          {cas.titre}
        </h1>

        <p className="text-gray-600 leading-relaxed mb-8">
          {cas.corps(sousDomaine)}
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <a
            href="https://klasys.com"
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-[#246990] text-white hover:bg-[#1d5674] transition-colors w-full sm:w-auto"
          >
            Aller sur klasys.com
          </a>
          <a
            href="mailto:support@klasys.com"
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors w-full sm:w-auto"
          >
            <LifeBuoy size={16} />
            Contacter le support
          </a>
        </div>

        <p className="text-xs text-gray-400 mt-10">
          KLASYS — by www.danicedgroup.com
        </p>
      </div>
    </div>
  );
};

export default EtablissementIndisponible;
